// Originally part of @splunk/react-visualizations-utils, this code replaces that package to consolidate things related to DataSet
import type {
    BindingType,
    DataSourceMeta,
    SearchData,
} from '@splunk/dashboard-types';
import DataSet from './DataSet';

export interface VizProps {
    dataSources?: Record<BindingType, SearchData>;
}

/**
 * Returns a dataset instance based on data from props
 * @param {object} vizProps
 * @param {string} [dataSourceType='primary']
 * @returns {DataSet | null}
 */
export function getDataSet(vizProps: VizProps, dataSourceType = 'primary') {
    const data = vizProps.dataSources?.[dataSourceType]?.data;
    return data ? DataSet.fromJSONCols(data.fields, data.columns) : null;
}

/**
 * Returns the dataset instance for dataSourceType = 'primary'
 * @param {object} vizProps
 * @returns {DataSet | null}
 */
export function getPrimaryDataSet(vizProps: VizProps) {
    return getDataSet(vizProps, 'primary');
}

/**
 * Returns the request parameters for a dataSourceType in vizProps
 * @param {object} vizProps visualization props object
 * @param {string} [dataSourceType='primary']
 * @returns {RequestParams | undefined}
 */
export function getRequestParams(
    vizProps: VizProps,
    dataSourceType = 'primary'
) {
    return vizProps.dataSources?.[dataSourceType]?.requestParams;
}

/**
 * Returns the request parameters for the primary data in vizProps
 * @param {object} vizProps visualization props object
 * @returns {RequestParams | undefined}
 */
export function getPrimaryRequestParams(vizProps: VizProps) {
    return getRequestParams(vizProps, 'primary');
}

const emptyMeta: DataSourceMeta = {};

/**
 * Returns the meta data for a dataSourceType in vizProps
 * @param {object} vizProps visualization props object
 * @param {string} [dataSourceType='primary']
 * @returns {DataSourceMeta}
 */
export function getMeta(vizProps: VizProps, dataSourceType = 'primary') {
    return vizProps.dataSources?.[dataSourceType]?.meta ?? emptyMeta;
}

/**
 * Returns the meta data for the primary data in vizProps
 * @param {object} vizProps visualization props object
 * @returns {DataSourceMeta}
 */
export function getPrimaryMeta(vizProps: VizProps) {
    return getMeta(vizProps, 'primary');
}
